// FLAGS: identity
// React Query bindings for the auth service, and the cache entry every gate reads the current user from.
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  enableAuthenticator,
  getTwoFactorRegistrationInfo,
  getUserProfile,
  loginWithGoogleCode,
  logout,
  registerWithGoogleCode,
  validateTotp,
} from "@src/services/authentication.service";
import { UserProfile } from "@src/models/auth";

export const authKeys = {
  me: ["auth", "me"] as const,
};

export const useCurrentUser = (): UseQueryResult<UserProfile | null> =>
  useQuery({
    queryKey: authKeys.me,
    queryFn: getUserProfile,
    retry: false,
    // Without this, an errored profile query re-fires whenever a new observer mounts, and the
    // guards mount one every time they swap between the spinner and their child. An errored query
    // has dataUpdatedAt === 0, so it is always stale: refetch -> pending -> guard renders the
    // spinner -> the child unmounts -> the query settles as error -> the child remounts -> refetch.
    // With the API unreachable that is an unbounded request loop, not a slow page.
    retryOnMount: false,
  });

// FLAGS: identity
// The profile is deliberately left un-refetched while `twoFactorVerification` is "pending", so a
// half-verified session never reaches the guards as a signed-in user.
export const useGoogleLoginMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: loginWithGoogleCode,
    onSuccess: async (response) => {
      if (response.twoFactorVerification === "not_required") {
        await queryClient.invalidateQueries({ queryKey: authKeys.me });
      }
    },
  });
};

/**
 * Google sign-up. Deliberately does NOT invalidate the current-user query:
 * the new account lacks the CustomerSuccess role, so instead of entering the
 * app the login page shows the "contact your administrator" message.
 */
export const useGoogleSignUpMutation = () =>
  useMutation({ mutationFn: registerWithGoogleCode });

export const useTotpMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: validateTotp,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

// FLAGS: identity
// The response is a TOTP shared secret drawn as a QR image. `gcTime: 0` drops it as soon as the step
// unmounts; raising it would keep one account's enrollment key in the cache for the next sign-in.
export const useTwoFactorRegistrationInfo = (): UseQueryResult<string | null> =>
  useQuery({
    queryKey: ["auth", "2fa-registration"],
    queryFn: getTwoFactorRegistrationInfo,
    // The QR embeds a freshly generated shared key — never serve a cached
    // one across logins.
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

export const useEnableAuthenticatorMutation = () =>
  useMutation({ mutationFn: enableAuthenticator });

// FLAGS: identity
// `onSettled`, not `onSuccess`: local state is cleared even when the server call fails, and every
// non-auth query is dropped so nothing of the previous user survives a sign-out on a shared machine.
export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.setQueryData(authKeys.me, null);
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "auth",
      });
    },
  });
};
